import { GameStore } from '@GameStore/GameStore';
import { EGameStoreKeys } from '@GameStore/GameStoreSchema';

import { Game } from '@Game/Game';
import EventsCenter from '@Managers/Events/EventsCenter';
import { EGameEvents } from '@Managers/Events/GameEvents';
import { EGameStates } from '@Managers/StateManager/GameStates';
import randomValue from '@Tools/RandomValue';

export class ReelSymbolsSetManager {
    public static readonly NUMBER_OF_RANDOM_SYMBOLS = 30;
    private readonly gameStore: GameStore;
    private reelSetCursors: number[] = [];
    private reelSets: number[][];
    private lockAndRollSpinningSets: {
        symbols: number[];
        coinMin?: number;
        coinMax?: number;
    }[] = [];
    private lockAndRollCursors: number[] = [];
    private lockAndRollReelSets: number[][] = [];
    private currentLockAndRollSet: number[] = [];

    constructor(game: Game) {
        this.gameStore = game.gameStore;
        this.gameStore.on(EGameStoreKeys.AuthenticateGameConfig, this.setReelSets, this);
        this.gameStore.on(EGameStoreKeys.NumberOfReels, this.setReelSets, this);
        EventsCenter.on(EGameStates.GAME_START, this.setDefaultLockAndRollSets, this);
        EventsCenter.on(EGameEvents.ACTION_RESPONSE_PROCESSED, this.changeLockAndRollSets, this);
    }

    public setReelSets(spinningReels?: number[][]): void {
        const fakeReelSets = Array.isArray(spinningReels) ? spinningReels : this.gameStore.get(EGameStoreKeys.AuthenticateGameConfig).spinningReels;
        const numberOfReels = this.gameStore.get(EGameStoreKeys.NumberOfReels);

        if (fakeReelSets?.length > 0) {
            this.reelSets = fakeReelSets;
        } else {
            const randomFakeReelSets = [];

            for (let i = 0; i < numberOfReels; i++) {
                const randomSet = new Array(ReelSymbolsSetManager.NUMBER_OF_RANDOM_SYMBOLS).fill(null).map((_) => {
                    return randomValue(1, this.gameStore.get(EGameStoreKeys.SymbolConfiguration).length - 1);
                });
                randomFakeReelSets.push(randomSet);
            }

            this.reelSets = randomFakeReelSets;
        }

        for (let i = 0; i < numberOfReels; i++) {
            this.reelSetCursors.push(randomValue(0, this.reelSets.length - 1));
        }
    }

    public setDefaultLockAndRollSets(): void {
        const numberOfReels = this.gameStore.get(EGameStoreKeys.NumberOfReels);
        const { spinningSets } = this.gameStore.get(EGameStoreKeys.LockAndRoll);

        for (let i = 0; i < numberOfReels; i++) {
            this.lockAndRollReelSets.push(this.reelSets[i]);
            this.lockAndRollCursors.push(randomValue(0, this.reelSets[i].length - 1));
        }

        if (spinningSets) this.lockAndRollSpinningSets = spinningSets;
    }

    public changeLockAndRollSets(): void {
        const { symbols } = this.gameStore.get(EGameStoreKeys.LockAndRoll);
        const coinCount = symbols.length;

        if (!this.lockAndRollSpinningSets || this.lockAndRollSpinningSets.length === 0) return;

        const newSet = this.lockAndRollSpinningSets.find((set) => {
            const isBiggerThanMin = set.coinMin && coinCount >= set.coinMin;
            const isSmallerThanMax = !set.coinMax || (set.coinMax && coinCount <= set.coinMax);

            return isBiggerThanMin && isSmallerThanMax;
        });

        if (!newSet || this.currentLockAndRollSet === newSet.symbols) return;

        this.currentLockAndRollSet = newSet.symbols;

        this.recreateLockAndRollSet();
    }

    public recreateLockAndRollSet(): void {
        const numberOfReels = this.gameStore.get(EGameStoreKeys.NumberOfReels);

        this.lockAndRollReelSets = [];
        this.lockAndRollCursors = [];
        for (let i = 0; i < numberOfReels; i++) {
            this.lockAndRollReelSets.push(this.currentLockAndRollSet);
            this.lockAndRollCursors.push(randomValue(0, this.currentLockAndRollSet.length - 1));
        }
    }

    private incrementReelSetCursor(reelIndex: number): void {
        this.reelSetCursors[reelIndex]++;
        if (this.reelSetCursors[reelIndex] > this.reelSets[reelIndex].length - 1) {
            this.reelSetCursors[reelIndex] = 0;
        }
    }

    private incrementLockAndRollSetCursor(reelIndex: number): void {
        this.lockAndRollCursors[reelIndex]++;
        if (this.lockAndRollCursors[reelIndex] > this.lockAndRollReelSets[reelIndex].length - 1) {
            this.lockAndRollCursors[reelIndex] = 0;
        }
    }

    public getIdFromReelSet(reelIndex: number): number {
        const isLockAndRoll = this.gameStore.get(EGameStoreKeys.IsLockAndRollMode);
        isLockAndRoll ? this.incrementLockAndRollSetCursor(reelIndex) : this.incrementReelSetCursor(reelIndex);

        return isLockAndRoll
            ? this.lockAndRollReelSets[reelIndex][this.lockAndRollCursors[reelIndex]]
            : this.reelSets[reelIndex][this.reelSetCursors[reelIndex]];
    }
}
